import React from 'react';

/**
 * Why section (#why) — "Useful long after checkout."
 * Four cards that pin and stack via pure CSS position:sticky with
 * staggered top offsets and spacer divs between them. No JavaScript.
 *
 * Each card escalates in color: dark -> green-tinted -> light -> full green.
 */

const mono = "'JetBrains Mono', ui-monospace, monospace";

// Corner icons per card (clock, shield-check, search, globe)
const ICONS = {
  clock: (stroke) => (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  shield: (stroke) => (
    <>
      <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  search: (stroke) => (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  globe: (stroke) => (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.4 2.5 3.7 5.7 3.7 9s-1.3 6.5-3.7 9c-2.4-2.5-3.7-5.7-3.7-9S9.6 5.5 12 3z" />
    </>
  ),
};

const CARDS = [
  {
    n: '01',
    icon: 'clock',
    title: 'Ready in 30 seconds',
    body: 'Get your KACHNG address and use it wherever you would normally give a merchant your email.',
    bg: 'radial-gradient(120% 100% at 20% 0%, #1A1E1B 0%, #0D0F0D 70%)',
    border: '#262A26',
    text: '#EDEFED',
    bodyColor: '#B9BEB9',
    numFaint: 'rgba(255,255,255,0.04)',
    iconStroke: 'rgba(255,255,255,0.07)',
    numSmall: '#3A403A',
    dot: '#00E676',
    top: '20vh',
  },
  {
    n: '02',
    icon: 'shield',
    title: 'Proof when you need it',
    body: 'Keep receipts, order details, and return deadlines ready when you need them.',
    bg: 'radial-gradient(120% 100% at 20% 0%, #143423 0%, #0B1B12 70%)',
    border: 'rgba(0,230,118,0.3)',
    text: '#EDEFED',
    bodyColor: '#C4DCCE',
    numFaint: 'rgba(0,230,118,0.06)',
    iconStroke: 'rgba(0,230,118,0.11)',
    numSmall: 'rgba(0,230,118,0.45)',
    dot: '#00E676',
    top: 'calc(20vh + 20px)',
  },
  {
    n: '03',
    icon: 'search',
    title: 'Findable later',
    body: 'Purchases are categorized automatically, so returns, expenses, and taxes do not become a scavenger hunt.',
    bg: 'radial-gradient(120% 100% at 20% 0%, #F2F4F2 0%, #DDE2DD 80%)',
    border: 'rgba(0,0,0,0.12)',
    text: '#0B0D0B',
    bodyColor: '#4A504A',
    numFaint: 'rgba(11,13,11,0.05)',
    iconStroke: 'rgba(11,13,11,0.09)',
    numSmall: 'rgba(11,13,11,0.28)',
    dot: '#00A857',
    top: 'calc(20vh + 40px)',
  },
  {
    n: '04',
    icon: 'globe',
    title: 'One less paper receipt',
    body: 'Choose digital receipts and help reduce unnecessary thermal paper. KACHNG currently funds one tree for every 100 eligible digital receipts processed.',
    bg: 'radial-gradient(120% 100% at 20% 0%, #00E676 0%, #00B85C 80%)',
    border: 'rgba(0,0,0,0.2)',
    text: '#052012',
    bodyColor: 'rgba(5,32,18,0.78)',
    numFaint: 'rgba(5,32,18,0.08)',
    iconStroke: 'rgba(5,32,18,0.13)',
    numSmall: 'rgba(5,32,18,0.4)',
    dot: '#052012',
    top: 'calc(20vh + 60px)',
  },
];

function StickyCard({ card }) {
  return (
    <div style={{ position: 'sticky', top: card.top }}>
      <div
        style={{
          background: card.bg,
          border: `1px solid ${card.border}`,
          borderRadius: '28px',
          padding: 'clamp(32px,4vw,64px)',
          minHeight: '56vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '32px',
          boxShadow: '0 -28px 72px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.12)',
          position: 'relative',
          overflow: 'hidden',
          color: card.text,
        }}
      >
        {/* Ghost number */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 'clamp(-20px,-1vw,0px)',
            bottom: '-0.28em',
            fontSize: 'clamp(180px,24vw,340px)',
            fontWeight: 800,
            letterSpacing: '-0.05em',
            lineHeight: 1,
            color: card.numFaint,
            pointerEvents: 'none',
            userSelect: 'none',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {card.n}
        </div>

        {/* Corner icon */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={card.iconStroke}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 'clamp(-64px,-4vw,-42px)',
            right: 'clamp(40px,4.5vw,84px)',
            width: 'clamp(160px,18vw,250px)',
            height: 'clamp(160px,18vw,250px)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {ICONS[card.icon]()}
        </svg>

        {/* Number + dot */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', position: 'relative' }}>
          <div
            style={{
              fontFamily: mono,
              fontSize: 'clamp(26px,2.8vw,36px)',
              fontWeight: 200,
              color: card.numSmall,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {card.n}
          </div>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: card.dot, flex: 'none' }} />
        </div>

        {/* Title + body */}
        <div style={{ position: 'relative' }}>
          <h3
            style={{
              margin: '0 0 18px',
              fontSize: 'clamp(36px,4.6vw,64px)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.02,
              textWrap: 'balance',
            }}
          >
            {card.title}
          </h3>
          <p style={{ margin: 0, fontSize: 'clamp(16px,1.6vw,20px)', lineHeight: 1.6, color: card.bodyColor, maxWidth: '50ch' }}>
            {card.body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Why({ mobile = false }) {
  if (mobile) return <section id="how-it-works" className="mobile-setup mobile-section-inner">
    <div className="mobile-kicker">Simple setup</div>
    <h2>Ready in 30 seconds.</h2>
    <ol>
      <li><span aria-hidden="true">01</span><p>Get your KACHNG address.</p></li>
      <li><span aria-hidden="true">02</span><p>Use it wherever you would normally provide your email.</p></li>
      <li><span aria-hidden="true">03</span><p>KACHNG turns incoming receipts into spending intelligence.</p></li>
    </ol>
  </section>;
  return (
    <section id="why">
      <div
        style={{
          padding: 'clamp(56px,7vw,88px) clamp(20px,5vw,56px)',
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <h2
          data-rv="up"
          style={{
            margin: '0 0 clamp(40px,5vw,64px)',
            fontSize: 'clamp(34px,4.4vw,56px)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.02,
            textTransform: 'uppercase',
          }}
        >
          Useful long after checkout.
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {CARDS.map((card, i) => (
            <React.Fragment key={card.n}>
              <StickyCard card={card} />
              {/* spacer between cards drives the stagger; smaller after the last */}
              <div style={{ height: i === CARDS.length - 1 ? '12vh' : '38vh', flex: 'none' }} />
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}